import { queryOptions, useQuery } from "@tanstack/react-query";
import { createContext, useContext } from "react";
import { api, type Org } from "~/core/api";
import { keys, read } from "~/core/query";

/**
 * The accounts this person is in, and the one they are looking at.
 *
 * A context rather than a prop threaded through the workspace, the layout and
 * the sidebar: exactly one component needs it — the switcher at the top of the
 * column that already answers "where am I" — and nothing between here and there
 * has any use for it.
 */
export const Workspaces = createContext<{ orgs: Org[]; current: Org | null; choose: (id: string) => void }>({
	orgs: [],
	current: null,
	choose: () => {},
});

export function useWorkspaces() {
	return useContext(Workspaces);
}

/**
 * The brands of one account, as both sidebars list them.
 *
 * One definition because there were two lists: the Agent read its own from a
 * route of its own, and the two halves of the product could name different
 * brands in the same switcher row.
 */
export function brandsQuery(org: string) {
	return queryOptions({ queryKey: keys.brands(org), queryFn: () => api.entities(org), ...read });
}

/**
 * The brands of the account on screen, from the entry the shell already holds.
 *
 * Not refetched on mount: the shell above both modules observes this list and
 * the account's events invalidate it, so a sidebar mounting is no news.
 */
export function useBrands() {
	const { current } = useWorkspaces();
	return useQuery({ ...brandsQuery(current?.id ?? ""), enabled: current !== null, refetchOnMount: false });
}

/**
 * Which account this browser is looking at.
 *
 * Everybody gets a personal workspace when they sign up, so somebody invited
 * into a colleague's account has two — and their own is the empty one the
 * wizard would send them to. Ordering by creation date and taking the first
 * would put a new member into an onboarding wizard for a workspace they have no
 * intention of using, instead of the account they were invited to.
 *
 * So: the one they last chose, or the first that has actually been set up.
 */
export function currentOrg(orgs: Org[], chosen: string): Org | undefined {
	return (
		orgs.find((org) => org.id === chosen) ??
		orgs.find((org) => org.onboarded_at !== null && org.entity_count > 0) ??
		orgs[0]
	);
}
